import { MissionStatus } from '../gcs/mission';
import { VehiclePreparationStatus } from '../gcs/vehicle';
import { en } from '../../locales/en';

export const VehicleStatus = {
  // No mission statuses
  PENDING_MISSION: 'pending_mission',
  PENDING_PREPARATION: 'pending_preparation',
  // Mission related statuses
  PENDING_PREFLIGHT: 'pending_preflight',
  READY_FOR_TAKEOFF: 'ready_for_takeoff',
  TAKING_OFF: 'taking_off',
  INFLIGHT_TO_PICKUP: 'inflight_to_pickup',
  PICKING_UP: 'picking_up',
  WAITING_FOR_PACKAGE_LOADED_CONFIRMATION: 'waiting_for_package_loaded_confirmation',
  INFLIGHT_TO_DOCK: 'inflight_to_dock',
  LANDING_ON_DOCK: 'landing_on_dock',
  WAITING_ON_DOCK: 'waiting_on_dock',
  READY_FOR_TAKEOFF_FROM_DOCK: 'ready_for_takeoff_from_dock',
  PICKING_UP_FROM_DOCK: 'picking_up_from_dock',
  TAKING_OFF_FROM_DOCK: 'taking_off_from_dock',
  INFLIGHT_TO_DESTINATION: 'inflight_to_destination',
  INFLIGHT_HOME: 'inflight_home',
  LANDING_HOME: 'landing_home',
  WAITING_FOR_WIRE_DROP_CONFIRMATION: 'waiting_for_wire_drop_confirmation',
  PERFORMING_WIRE_DROP: 'performing_wire_drop',
  HOVERING_PAUSED: 'hovering_paused',
  SAFING_UP: 'safing_up',
  SAFED_UP: 'safed_up',
  DESCENDING: 'descending',
  DESCENDED: 'descended',
  ABORT_HOME: 'abort_home',
  FC_RTL: 'fc_rtl',
  EMERGENCY_LANDING: 'emergency_landing',
  RC_IN_CONTROL: 'rc_in_control',
  MISSION_COMPUTER_POSTFLIGHT: 'mission_computer_postflight',
  EXECUTING_FALLBACK_MISSION: 'executing_fallback_mission',
  FC_LOSS: 'fc_loss',
  PENDING_POSTFLIGHT: 'pending_postflight',
  PARACHUTE_TRIGGERED: 'parachute_triggered',
  VEHICLE_DISCONNECTED: 'vehicle_disconnected'
} as const;

export type VehicleStatus = (typeof VehicleStatus)[keyof typeof VehicleStatus];

interface CalculateVehicleStatusParams {
  missionStatus?: MissionStatus;
  vehiclePreparationStatus?: VehiclePreparationStatus;
  isReadyForTakeoffFromLaunchpad: boolean;
  isReadyForTakeoffFromDock: boolean;
}

export function calculateVehicleStatus({
  missionStatus,
  vehiclePreparationStatus,
  isReadyForTakeoffFromLaunchpad,
  isReadyForTakeoffFromDock
}: CalculateVehicleStatusParams): VehicleStatus {
  switch (missionStatus) {
    case MissionStatus.CREATED:
    case MissionStatus.PREFLIGHT:
      if (isReadyForTakeoffFromLaunchpad) return VehicleStatus.READY_FOR_TAKEOFF;
      return VehicleStatus.PENDING_PREFLIGHT;
    case MissionStatus.TAKING_OFF:
      return VehicleStatus.TAKING_OFF;
    case MissionStatus.INFLIGHT_TO_PICKUP:
      return VehicleStatus.INFLIGHT_TO_PICKUP;
    case MissionStatus.INFLIGHT_TO_DESTINATION:
      return VehicleStatus.INFLIGHT_TO_DESTINATION;
    case MissionStatus.INFLIGHT_HOME:
      return VehicleStatus.INFLIGHT_HOME;
    case MissionStatus.PICKING_UP:
      return VehicleStatus.PICKING_UP;
    case MissionStatus.WAITING_FOR_PACKAGE_LOADED_CONFIRMATION:
      return VehicleStatus.WAITING_FOR_PACKAGE_LOADED_CONFIRMATION;
    case MissionStatus.INFLIGHT_TO_DOCK:
      return VehicleStatus.INFLIGHT_TO_DOCK;
    case MissionStatus.LANDING_ON_DOCK:
      return VehicleStatus.LANDING_ON_DOCK;
    case MissionStatus.WAITING_ON_DOCK:
      if (isReadyForTakeoffFromDock) return VehicleStatus.READY_FOR_TAKEOFF_FROM_DOCK;
      return VehicleStatus.WAITING_ON_DOCK;
    case MissionStatus.PICKING_UP_FROM_DOCK:
      return VehicleStatus.PICKING_UP_FROM_DOCK;
    case MissionStatus.TAKING_OFF_FROM_DOCK:
      return VehicleStatus.TAKING_OFF_FROM_DOCK;
    case MissionStatus.WAITING_FOR_WIRE_DROP_CONFIRMATION:
      return VehicleStatus.WAITING_FOR_WIRE_DROP_CONFIRMATION;
    case MissionStatus.PERFORMING_WIRE_DROP:
      return VehicleStatus.PERFORMING_WIRE_DROP;
    case MissionStatus.LANDING_HOME:
      return VehicleStatus.LANDING_HOME;
    case MissionStatus.POSTFLIGHT:
      return VehicleStatus.PENDING_POSTFLIGHT;
    case MissionStatus.MISSION_COMPUTER_POSTFLIGHT:
      return VehicleStatus.MISSION_COMPUTER_POSTFLIGHT;
    case MissionStatus.EMERGENCY_LANDING:
      return VehicleStatus.EMERGENCY_LANDING;
    case MissionStatus.PARACHUTE_TRIGGERED:
      return VehicleStatus.PARACHUTE_TRIGGERED;
    case MissionStatus.FC_LOSS:
      return VehicleStatus.FC_LOSS;
    case MissionStatus.VEHICLE_DISCONNECTED:
      return VehicleStatus.VEHICLE_DISCONNECTED;
    case MissionStatus.HOVERING_PAUSED:
      return VehicleStatus.HOVERING_PAUSED;
    case MissionStatus.SAFING_UP:
      return VehicleStatus.SAFING_UP;
    case MissionStatus.SAFED_UP:
      return VehicleStatus.SAFED_UP;
    case MissionStatus.DESCENDING:
      return VehicleStatus.DESCENDING;
    case MissionStatus.DESCENDED:
      return VehicleStatus.DESCENDED;
    case MissionStatus.ABORT_HOME:
      return VehicleStatus.ABORT_HOME;
    case MissionStatus.FC_RTL:
      return VehicleStatus.FC_RTL;
    case MissionStatus.RC_IN_CONTROL:
      return VehicleStatus.RC_IN_CONTROL;
    case MissionStatus.EXECUTING_FALLBACK_MISSION:
      return VehicleStatus.EXECUTING_FALLBACK_MISSION;
    case MissionStatus.COMPLETED:
    case MissionStatus.ABORTED:
    case undefined:
      if (vehiclePreparationStatus === VehiclePreparationStatus.PENDING_PREPARATION)
        return VehicleStatus.PENDING_PREPARATION;
      return VehicleStatus.PENDING_MISSION;
  }
}

export function getVehicleStatusLabel(status: VehicleStatus): string {
  switch (status) {
    case VehicleStatus.PENDING_MISSION:
      return en.vehicleStatus.pendingMission;
    case VehicleStatus.PENDING_PREPARATION:
      return en.vehicleStatus.pendingPreparation;
    case VehicleStatus.PENDING_PREFLIGHT:
      return en.vehicleStatus.pendingPreflight;
    case VehicleStatus.READY_FOR_TAKEOFF:
      return en.vehicleStatus.readyForTakeoff;
    case VehicleStatus.TAKING_OFF:
      return en.vehicleStatus.takingOff;
    case VehicleStatus.INFLIGHT_TO_PICKUP:
      return en.vehicleStatus.inflightToPickup;
    case VehicleStatus.PICKING_UP:
      return en.vehicleStatus.pickingUp;
    case VehicleStatus.WAITING_FOR_PACKAGE_LOADED_CONFIRMATION:
      return en.vehicleStatus.waitingForPackageLoadedConfirmation;
    case VehicleStatus.INFLIGHT_TO_DOCK:
      return en.vehicleStatus.inflightToDock;
    case VehicleStatus.LANDING_ON_DOCK:
      return en.vehicleStatus.landingOnDock;
    case VehicleStatus.WAITING_ON_DOCK:
      return en.vehicleStatus.waitingOnDock;
    case VehicleStatus.READY_FOR_TAKEOFF_FROM_DOCK:
      return en.vehicleStatus.readyForTakeoffFromDock;
    case VehicleStatus.PICKING_UP_FROM_DOCK:
      return en.vehicleStatus.pickingUpFromDock;
    case VehicleStatus.TAKING_OFF_FROM_DOCK:
      return en.vehicleStatus.takingOffFromDock;
    case VehicleStatus.INFLIGHT_TO_DESTINATION:
      return en.vehicleStatus.inflightToDestination;
    case VehicleStatus.INFLIGHT_HOME:
      return en.vehicleStatus.inflightHome;
    case VehicleStatus.LANDING_HOME:
      return en.vehicleStatus.landingHome;
    case VehicleStatus.WAITING_FOR_WIRE_DROP_CONFIRMATION:
      return en.vehicleStatus.waitingForWireDropConfirmation;
    case VehicleStatus.PERFORMING_WIRE_DROP:
      return en.vehicleStatus.performingWireDrop;
    case VehicleStatus.HOVERING_PAUSED:
      return en.vehicleStatus.hoveringPaused;
    case VehicleStatus.SAFING_UP:
      return en.vehicleStatus.safingUp;
    case VehicleStatus.SAFED_UP:
      return en.vehicleStatus.safedUp;
    case VehicleStatus.DESCENDING:
      return en.vehicleStatus.descending;
    case VehicleStatus.DESCENDED:
      return en.vehicleStatus.descended;
    case VehicleStatus.ABORT_HOME:
      return en.vehicleStatus.abortHome;
    case VehicleStatus.FC_RTL:
      return en.vehicleStatus.fcRtl;
    case VehicleStatus.EMERGENCY_LANDING:
      return en.vehicleStatus.emergencyLanding;
    case VehicleStatus.RC_IN_CONTROL:
      return en.vehicleStatus.rcInControl;
    case VehicleStatus.MISSION_COMPUTER_POSTFLIGHT:
      return en.vehicleStatus.mcPostflight;
    case VehicleStatus.EXECUTING_FALLBACK_MISSION:
      return en.vehicleStatus.executingFallbackMission;
    case VehicleStatus.FC_LOSS:
      return en.vehicleStatus.fcLoss;
    case VehicleStatus.PENDING_POSTFLIGHT:
      return en.vehicleStatus.postflight;
    case VehicleStatus.PARACHUTE_TRIGGERED:
      return en.vehicleStatus.parachuteTriggered;
    case VehicleStatus.VEHICLE_DISCONNECTED:
      return en.vehicleStatus.vehicleDisconnected;
  }
}
